import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChromeReaderMode, ChromeReaderModeOutlined } from '@mui/icons-material'
import { Box, Button, Typography } from '@mui/material'
import PropTypes from 'prop-types'

/**
 * Header component for the ItemSettings section.
 * Displays the "Settings" title and a "Part notes" button.
 * Memoized to prevent unnecessary re-renders when parent state changes.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.partHasNotes - Whether the part has any notes (customer, vendor, or private)
 * @param {boolean} [props.disabled=false] - Whether the notes button is disabled
 * @param {Function} props.onNotesClick - Callback when the notes button is clicked
 * @returns {React.ReactElement} The settings header with title and notes button
 */
const SettingsHeader = ({ disabled = false, onNotesClick, partHasNotes }) => {
    const { t } = useTranslation()

    return (
        <Box
            sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
            }}
        >
            <Typography
                data-testid="quote-item-settings-section-title"
                style={{ fontWeight: 700 }}
                variant="body1"
            >
                {t('Settings')}
            </Typography>
            <Button
                color="secondary"
                data-testid="nqp-quote-item-notes-button"
                disabled={disabled}
                size="small"
                startIcon={partHasNotes ? <ChromeReaderMode /> : <ChromeReaderModeOutlined />}
                variant="text"
                onClick={(e) => onNotesClick(e)}
            >
                {t('Part notes')}
            </Button>
        </Box>
    )
}

SettingsHeader.propTypes = {
    partHasNotes: PropTypes.bool.isRequired,
    onNotesClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
}

export default memo(SettingsHeader)
